import { z as r } from "zod";
class h {
  handlers = /* @__PURE__ */ new Map();
  on(t, e) {
    let o = this.handlers.get(t);
    return o || (o = /* @__PURE__ */ new Set(), this.handlers.set(t, o)), o.add(e), () => this.off(t, e);
  }
  off(t, e) {
    this.handlers.get(t)?.delete(e);
  }
  emit(t, e) {
    const o = this.handlers.get(t);
    if (o)
      for (const s of o)
        s(e);
  }
  clear() {
    this.handlers.clear();
  }
}
class p {
  factories = /* @__PURE__ */ new Map();
  register(t, e) {
    this.factories.set(t, e);
  }
  has(t) {
    return this.factories.has(t);
  }
  async create(t) {
    const e = this.factories.get(t);
    if (!e)
      throw new Error(`Unknown view mode: "${t}". Registered: ${this.list().join(", ")}`);
    return e();
  }
  list() {
    return [...this.factories.keys()];
  }
}
const a = r.tuple([r.number(), r.number(), r.number()]), m = r.object({
  /** CSS color or `transparent`. */
  color: r.string().default("#f5f5f5")
}).default({ color: "#f5f5f5" }), g = r.object({
  /** Show the zoom in/out controls. */
  zoom: r.boolean().default(!0),
  /** Show the fullscreen toggle. */
  fullscreen: r.boolean().default(!0),
  /** Show the reset-view control. */
  reset: r.boolean().default(!0)
}).default({ zoom: !0, fullscreen: !0, reset: !0 }), b = r.object({
  /** Optional logo shown as a small watermark. */
  logoUrl: r.string().url().optional(),
  /** Accent color used by interactive controls. */
  accentColor: r.string().default("#2563eb")
}).default({ accentColor: "#2563eb" }), w = {
  background: m,
  ui: g,
  branding: b
}, v = r.object({
  type: r.enum(["ambient", "directional", "point", "hemisphere"]),
  color: r.string().default("#ffffff"),
  intensity: r.number().default(1),
  position: a.optional(),
  /**
   * Lock this light to the camera so it stays fixed relative to the view (a
   * "headlight") instead of being world-fixed and sliding across the product as the
   * camera orbits. Only affects `directional` and `point` lights.
   */
  followCamera: r.boolean().default(!1)
}), y = r.object({
  position: a.default([0, 0, 4]),
  target: a.default([0, 0, 0]),
  fov: r.number().min(1).max(179).default(45),
  minDistance: r.number().default(0.5),
  maxDistance: r.number().default(50),
  autoRotate: r.boolean().default(!1)
}).default({}), x = r.object({
  /** Part name as authored in CAD / the model file (e.g. `face`, `handle`). */
  name: r.string(),
  /** What the customer can do with this slot. */
  role: r.enum(["artwork", "color", "fixed"]).default("color"),
  /** Default flat color for `color` slots. */
  defaultColor: r.string().optional(),
  /** Human-friendly label shown in the UI. */
  label: r.string().optional(),
  /** Surface roughness, 0 (mirror) to 1 (fully matte). Applied to this slot's material. */
  roughness: r.number().min(0).max(1).optional(),
  /**
   * How customer artwork is oriented on this slot's surface. Only used for `artwork`
   * slots; lets you correct mirroring/rotation from the model's winding without
   * re-exporting CAD.
   */
  artwork: r.object({
    /** Mirror horizontally (flip U). Use if text comes out backwards. */
    flipU: r.boolean().default(!1),
    /** Mirror vertically (flip V). Use if the image is upside down. */
    flipV: r.boolean().default(!1),
    /** Rotate the artwork on the surface, in degrees. */
    rotation: r.number().default(0),
    /** Scale the artwork (1 = fill the slot). >1 zooms in, <1 zooms out. */
    scale: r.number().min(0.01).default(1),
    /** Shift the artwork across the surface, in UV units (0–1). */
    offset: r.tuple([r.number(), r.number()]).default([0, 0])
  }).default({})
}), _ = r.object({
  url: r.string(),
  /** Explicit format hint; otherwise inferred from the URL extension. */
  format: r.enum(["glb", "gltf", "obj"]).optional(),
  /**
   * Up axis of the source model. CAD tools like Fusion 360 export Z-up, while the
   * viewer is Y-up — set this to `'z'` to auto-correct the common "lying on its
   * back, rotated 90° on X" look. Applied before {@link rotation}.
   */
  upAxis: r.enum(["y", "z"]).default("y"),
  /** Extra rotation in degrees `[x, y, z]`, applied after the up-axis correction. */
  rotation: r.tuple([r.number(), r.number(), r.number()]).default([0, 0, 0]),
  /**
   * Skip loading the sibling `.mtl` file for OBJ models. Use this when the MTL
   * overrides materials in a way that breaks slot colour/artwork controls.
   */
  noMtl: r.boolean().default(!1)
}), k = r.object({
  mode: r.literal("3d"),
  model: _,
  camera: y,
  lights: r.array(v).default([
    { type: "ambient", color: "#ffffff", intensity: 0.6 },
    { type: "directional", color: "#ffffff", intensity: 1, position: [5, 10, 7] }
  ]),
  /** Optional environment/HDRI for image-based lighting and reflections. */
  environment: r.object({
    hdriUrl: r.string().url().optional(),
    /** Use the environment as the visible background. */
    asBackground: r.boolean().default(!1)
  }).optional(),
  /**
   * Explicit slot mapping. When omitted, parts are auto-detected from their names
   * using {@link defaultSlotRole}.
   */
  slots: r.array(x).optional(),
  /** Allow the customer to upload their own model. */
  allowModelUpload: r.boolean().default(!1),
  ...w
}), E = k;
function l(n) {
  const t = E.safeParse(n);
  return t.success ? { success: !0, config: t.data } : { success: !1, error: t.error.issues.map((o) => `${o.path.join(".") || "(root)"}: ${o.message}`).join("; ") };
}
class S {
  root;
  content;
  toolbar;
  overlay;
  constructor(t, e) {
    if (this.root = document.createElement("div"), this.root.className = "pa-previewer", this.root.style.setProperty("--pa-accent", e.branding.accentColor), this.root.style.setProperty("--pa-bg", e.background.color), this.content = document.createElement("div"), this.content.className = "pa-previewer__content", this.toolbar = document.createElement("div"), this.toolbar.className = "pa-previewer__toolbar", this.overlay = document.createElement("div"), this.overlay.className = "pa-previewer__overlay", this.overlay.hidden = !0, this.root.append(this.content, this.toolbar, this.overlay), e.branding.logoUrl) {
      const o = document.createElement("img");
      o.className = "pa-previewer__logo", o.src = e.branding.logoUrl, o.alt = "", this.root.append(o);
    }
    t.append(this.root);
  }
  /** Render the toolbar from a set of actions, replacing any existing buttons. */
  setActions(t) {
    this.toolbar.replaceChildren();
    for (const e of t) {
      const o = document.createElement("button");
      o.type = "button", o.className = "pa-previewer__btn", o.title = e.label, o.setAttribute("aria-label", e.label), o.textContent = e.icon, o.addEventListener("click", e.onClick), this.toolbar.append(o);
    }
  }
  /** Show a centered overlay message (loading or error states). */
  showOverlay(t, e = "loading") {
    this.overlay.hidden = !1, this.overlay.dataset.kind = e, this.overlay.textContent = t;
  }
  hideOverlay() {
    this.overlay.hidden = !0, this.overlay.textContent = "";
  }
  dispose() {
    this.root.remove();
  }
}
async function L(n) {
  return document.fullscreenElement ? (await document.exitFullscreen(), !1) : (await n.requestFullscreen(), !0);
}
const z = `
.pa-previewer {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 240px;
    overflow: hidden;
    background: var(--pa-bg, #f5f5f5);
    font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    color: #1f2937;
    user-select: none;
    -webkit-user-select: none;
}
.pa-previewer__content {
    position: absolute;
    inset: 0;
}
.pa-previewer__toolbar {
    position: absolute;
    right: 12px;
    bottom: 12px;
    display: flex;
    gap: 6px;
    z-index: 2;
}
.pa-previewer__btn {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.9);
    color: #1f2937;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    transition: background 0.15s ease;
}
.pa-previewer__btn:hover {
    background: #fff;
}
.pa-previewer__btn:active {
    background: var(--pa-accent, #2563eb);
    color: #fff;
}
.pa-previewer__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    text-align: center;
    background: rgba(245, 245, 245, 0.85);
    backdrop-filter: blur(2px);
    font-size: 14px;
    z-index: 3;
}
.pa-previewer__overlay[data-kind='error'] {
    color: #b91c1c;
}
.pa-previewer__overlay[hidden] {
    display: none;
}
.pa-previewer__logo {
    position: absolute;
    left: 12px;
    top: 12px;
    max-height: 28px;
    opacity: 0.85;
    z-index: 2;
    pointer-events: none;
}
`, c = "pa-previewer-styles";
function j(n = document) {
  if (n.getElementById(c)) return;
  const t = n.createElement("style");
  t.id = c, t.textContent = z, n.head.append(t);
}
class d {
  events = new h();
  registry;
  host;
  shell;
  mode;
  config;
  resizeObserver;
  disposed = !1;
  /**
   * A promise that resolves when the viewer has finished mounting, or rejects on
   * config/mount failure. Useful when you want to `await` initialisation:
   * ```ts
   * const p = new Previews(el, config);
   * await p.ready;
   * ```
   */
  ready;
  /**
   * Create a `Previews` instance. Mounting starts immediately in the background.
   * Listen for `'ready'` / `'error'` events, or `await p.ready`.
   */
  constructor(t, e, o = {}) {
    const s = typeof t == "string" ? document.querySelector(t) : t;
    if (!s) throw new Error(`Previews: element "${String(t)}" not found.`);
    this.host = s, this.registry = o.registry ?? new p(), this.ready = this.start(e), this.ready.catch(() => {
    });
  }
  /**
   * Async factory — validates `config`, mounts, and resolves with the live instance.
   * Rejects on invalid config or a failed mount.
   */
  static async create(t, e, o = {}) {
    const s = new d(t, e, o);
    return await s.ready, s;
  }
  async start(t) {
    const e = l(t);
    if (!e.success) {
      const o = new Error(`Invalid config: ${e.error}`);
      throw this.fail(o), o;
    }
    this.config = e.config, j(this.host.ownerDocument), this.shell = new S(this.host, e.config), this.shell.showOverlay("Loading…", "loading");
    try {
      this.mode = await this.registry.create(e.config.mode);
    } catch (o) {
      throw this.fail(o), o;
    }
    try {
      await this.mode.mount(e.config, {
        container: this.shell.content,
        events: this.events
      }), this.shell.hideOverlay(), this.observeResize(), this.events.emit("ready", { mode: e.config.mode });
    } catch (o) {
      throw this.fail(o), o;
    }
  }
  observeResize() {
    !this.mode?.resize || !this.shell || (this.resizeObserver = new ResizeObserver(() => this.mode?.resize?.()), this.resizeObserver.observe(this.shell.root));
  }
  fail(t) {
    const e = t instanceof Error ? t.message : String(t);
    this.shell?.showOverlay(e, "error"), this.events.emit("error", { message: e, fatal: !0 });
  }
  /** Subscribe to a previews event. Returns an unsubscribe function. */
  on(t, e) {
    return this.events.on(t, e);
  }
  /**
   * The active 3D mode instance. Cast to `ThreeViewMode` to access `setColor`,
   * `setArtwork`, etc. Returns `undefined` before mount or after dispose.
   */
  getMode() {
    return this.mode;
  }
  /** Apply an updated config (must keep the same mode). */
  async update(t) {
    if (this.disposed) throw new Error("Previews has been disposed.");
    const e = l(t);
    if (!e.success) throw new Error(`Invalid config: ${e.error}`);
    if (e.config.mode !== this.config?.mode)
      throw new Error("Cannot change mode via update(); create a new Previews instance.");
    this.config = e.config, await this.mode?.update?.(e.config);
  }
  /** Dispose the viewer, cleaning up DOM, observers, and listeners. Idempotent. */
  dispose() {
    this.disposed || (this.disposed = !0, this.resizeObserver?.disconnect(), this.mode?.dispose(), this.shell?.dispose(), this.events.clear());
  }
}
const R = 25 * 1024 * 1024;
async function M(n, t = {}) {
  const { maxBytes: e = R, accept: o = ["image/"] } = t;
  let s, i = !1;
  if (typeof n == "string")
    s = n;
  else {
    if (n.size > e)
      throw new Error(
        `Image is too large (${f(n.size)}); max is ${f(e)}.`
      );
    if (!o.some((u) => n.type.startsWith(u)))
      throw new Error(`Unsupported image type: "${n.type || "unknown"}".`);
    s = URL.createObjectURL(n), i = !0;
  }
  try {
    return await C(s);
  } finally {
    i && URL.revokeObjectURL(s);
  }
}
function C(n) {
  return new Promise((t, e) => {
    const o = new Image();
    o.crossOrigin = "anonymous", o.onload = () => t(o), o.onerror = () => e(new Error(`Failed to load image: ${n}`)), o.src = n;
  });
}
function f(n) {
  if (n < 1024) return `${n} B`;
  const t = ["KB", "MB", "GB"];
  let e = n / 1024, o = 0;
  for (; e >= 1024 && o < t.length - 1; )
    e /= 1024, o++;
  return `${e.toFixed(1)} ${t[o]}`;
}
const O = /* @__PURE__ */ new Set(["face", "front", "print", "artwork", "canvas", "label"]), U = ["print_", "artwork_", "print-", "artwork-"];
function $(n) {
  const t = n.trim().toLowerCase();
  return O.has(t) || U.some((e) => t.startsWith(e)) ? "artwork" : "color";
}
const A = {
  flipU: !1,
  flipV: !1,
  rotation: 0,
  scale: 1,
  offset: [0, 0]
};
function I(n, t) {
  const e = /* @__PURE__ */ new Map();
  for (const o of n)
    e.set(o, { name: o, role: $(o), artwork: A });
  for (const o of t ?? [])
    e.set(o.name, { ...e.get(o.name), ...o });
  return [...e.values()];
}
export {
  h as EventBus,
  p as ModeRegistry,
  d as Previews,
  S as Shell,
  k as config3dSchema,
  $ as defaultSlotRole,
  f as formatBytes,
  j as injectStyles,
  M as loadImage,
  l as parseConfig,
  E as previewsConfigSchema,
  I as resolveSlots,
  L as toggleFullscreen
};
//# sourceMappingURL=index.js.map
